/**
 * Sample messages for a scheme profile, unsigned, in its pinned versions: a
 * payment, a status request, and an answer to an incoming payment. Two shapes:
 * the Sandbox's own (business message, BIC agents, UETR), and a Montran-built
 * scheme's such as PAPSS (member ids, clearing system, group-level answers).
 * The parties are invented test parties; amounts and identifiers come from the
 * caller, so a sample can carry an accepted FX quote.
 */

const ISO = 'urn:iso:std:iso:20022:tech:xsd:';

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Now, as ISO 20022 writes it: whole seconds, UTC. */
export function isoNow(date = new Date()) {
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/** A short random reference, upper-case: for message, instruction and end-to-end ids. */
export function ref(length = 10) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  const bytes = (globalThis.crypto?.getRandomValues?.(new Uint8Array(length))) || [];
  for (let i = 0; i < length; i++) out += chars[(bytes[i] ?? Math.floor(Math.random() * 256)) % chars.length];
  return out;
}

/** A UETR: a lower-case UUID version 4, as the reference profile's quotes are bound by. */
export function uetr() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const b = Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = b.map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Whether the profile speaks a Montran-built scheme's format (PAPSS). */
export const isMontran = (summary) => summary?.wire?.envelope === 'MONTRAN_MESSAGE';

/** Invented test agents: a bank in each country, by BIC (Sandbox format) or member id (Montran). */
export const testBic = (country, side) => `SBX${side === 'creditor' ? 'B' : 'A'}${country}${side === 'creditor' ? 'NA' : country}XXX`;
export const testMember = (country) => `${country}0001`;

const amt = (tag, money) => `<${tag} Ccy="${esc(money.currency)}">${esc(money.amount)}</${tag}>`;

/**
 * A payment from the participant into a corridor.
 *
 * @param p.summary   the profile summary (versions, wire format, clearing system)
 * @param p.from      the participant's home country; p.to the destination
 * @param p.send      { amount, currency } the interbank settlement amount
 * @param p.instructed optional { amount, currency } the instructed amount (a fixed receive)
 * @param p.xchgRate  optional exchange rate, as the quote gives it
 * @param p.uetr      the UETR (Sandbox format), p.memberId the participant's member id (Montran)
 */
export function creditSample(p) {
  const version = p.summary.messages['pacs.008'];
  const id = p.id || ref();
  const created = p.created || isoNow();
  if (isMontran(p.summary)) {
    const cs = esc(p.summary.wire.clearingSystem || 'PAPSS');
    const m = (mmb) => `<FinInstnId><ClrSysMmbId><ClrSysId><Prtry>${cs}</Prtry></ClrSysId><MmbId>${esc(mmb)}</MmbId></ClrSysMmbId></FinInstnId>`;
    return `<Document xmlns="${ISO}${version}"><FIToFICstmrCdtTrf>`
      + `<GrpHdr><MsgId>${esc(p.memberId)}-M-${id}</MsgId><CreDtTm>${created}</CreDtTm><NbOfTxs>1</NbOfTxs>`
      + `<SttlmInf><SttlmMtd>CLRG</SttlmMtd><ClrSys><Prtry>${cs}</Prtry></ClrSys></SttlmInf>`
      + `<PmtTpInf><SvcLvl><Cd>INST</Cd></SvcLvl><LclInstrm><Cd>SSA3</Cd></LclInstrm></PmtTpInf>`
      + `<InstgAgt>${m(p.memberId)}</InstgAgt></GrpHdr>`
      + `<CdtTrfTxInf><PmtId><InstrId>${esc(p.memberId)}-I-${id}</InstrId><EndToEndId>${esc(p.memberId)}-E-${id}</EndToEndId>`
      + `<TxId>${esc(p.memberId)}-T-${id}</TxId></PmtId>`
      + amt('IntrBkSttlmAmt', p.send) + (p.instructed ? amt('InstdAmt', p.instructed) : '')
      + `<ChrgBr>SLEV</ChrgBr>`
      + `<Dbtr><Nm>Test Originator</Nm><PstlAdr><Ctry>${esc(p.from)}</Ctry><AdrLine>Test street 1</AdrLine></PstlAdr></Dbtr>`
      + `<DbtrAcct><Id><Othr><Id>000111222</Id><SchmeNm><Cd>BBAN</Cd></SchmeNm></Othr></Id></DbtrAcct>`
      + `<DbtrAgt>${m(p.memberId)}</DbtrAgt><CdtrAgt>${m(testMember(p.to))}</CdtrAgt>`
      + `<Cdtr><Nm>Test Beneficiary</Nm><PstlAdr><Ctry>${esc(p.to)}</Ctry><AdrLine>Test avenue 2</AdrLine></PstlAdr></Cdtr>`
      + `<CdtrAcct><Id><Othr><Id>333444555</Id><SchmeNm><Cd>BBAN</Cd></SchmeNm></Othr></Id></CdtrAcct>`
      + `<RmtInf><Ustrd>Sandbox sample payment ${id}</Ustrd></RmtInf>`
      + `</CdtTrfTxInf></FIToFICstmrCdtTrf></Document>`;
  }
  return `<Document xmlns="${ISO}${version}"><FIToFICstmrCdtTrf>`
    + `<GrpHdr><MsgId>M-${id}</MsgId><CreDtTm>${created}</CreDtTm><NbOfTxs>1</NbOfTxs>`
    + `<SttlmInf><SttlmMtd>CLRG</SttlmMtd></SttlmInf></GrpHdr>`
    + `<CdtTrfTxInf><PmtId><EndToEndId>E-${id}</EndToEndId><TxId>T-${id}</TxId><UETR>${esc(p.uetr)}</UETR></PmtId>`
    + amt('IntrBkSttlmAmt', p.send)
    + (p.instructed ? amt('InstdAmt', p.instructed) : '')
    + (p.xchgRate && p.instructed ? `<XchgRate>${esc(p.xchgRate)}</XchgRate>` : '')
    + `<ChrgBr>SLEV</ChrgBr>`
    + `<Dbtr><Nm>Test Originator</Nm><PstlAdr><TwnNm>Test Town</TwnNm><Ctry>${esc(p.from)}</Ctry></PstlAdr></Dbtr>`
    + `<DbtrAcct><Id><Othr><Id>000111222</Id></Othr></Id></DbtrAcct>`
    + `<DbtrAgt><FinInstnId><BICFI>${testBic(p.from, 'debtor')}</BICFI></FinInstnId></DbtrAgt>`
    + `<CdtrAgt><FinInstnId><BICFI>${testBic(p.to, 'creditor')}</BICFI></FinInstnId></CdtrAgt>`
    + `<Cdtr><Nm>Test Beneficiary</Nm><PstlAdr><TwnNm>Test City</TwnNm><Ctry>${esc(p.to)}</Ctry></PstlAdr></Cdtr>`
    + `<CdtrAcct><Id><Othr><Id>333444555</Id></Othr></Id></CdtrAcct>`
    + `<Purp><Cd>SALA</Cd></Purp>`
    + `<RmtInf><Ustrd>Sandbox sample payment ${id}</Ustrd></RmtInf>`
    + `</CdtTrfTxInf></FIToFICstmrCdtTrf></Document>`;
}

/** A status request about a payment: by its message and end-to-end ids. */
export function statusRequestSample({ summary, orgnlMsgId, orgnlEndToEndId, memberId }) {
  const version = summary.messages['pacs.028'];
  const id = ref();
  const prefix = isMontran(summary) ? `${memberId}-S-` : 'S-';
  return `<Document xmlns="${ISO}${version}"><FIToFIPmtStsReq>`
    + `<GrpHdr><MsgId>${esc(prefix + id)}</MsgId><CreDtTm>${isoNow()}</CreDtTm></GrpHdr>`
    + (orgnlMsgId ? `<OrgnlGrpInf><OrgnlMsgId>${esc(orgnlMsgId)}</OrgnlMsgId>`
      + `<OrgnlMsgNmId>${esc(summary.messages['pacs.008'])}</OrgnlMsgNmId></OrgnlGrpInf>` : '')
    + `<TxInf><OrgnlEndToEndId>${esc(orgnlEndToEndId)}</OrgnlEndToEndId></TxInf>`
    + `</FIToFIPmtStsReq></Document>`;
}

/**
 * An answer to an incoming payment: for the group (Montran) or the transaction
 * (Sandbox format), with a reason code when it refuses.
 */
export function answerSample({ summary, payment, status, reason, memberId }) {
  const version = summary.messages['pacs.002'];
  const id = ref();
  const why = reason ? `<StsRsnInf><Rsn><Cd>${esc(reason)}</Cd></Rsn></StsRsnInf>` : '';
  const head = `<Document xmlns="${ISO}${version}"><FIToFIPmtStsRpt>`
    + `<GrpHdr><MsgId>${esc(isMontran(summary) ? `${memberId}-A-${id}` : `A-${id}`)}</MsgId><CreDtTm>${isoNow()}</CreDtTm></GrpHdr>`;
  if (summary.replies?.level === 'GROUP') {
    return head + `<OrgnlGrpInfAndSts><OrgnlMsgId>${esc(payment.msgId)}</OrgnlMsgId>`
      + `<OrgnlMsgNmId>${esc(payment.msgNmId || summary.messages['pacs.008'])}</OrgnlMsgNmId>`
      + `<GrpSts>${esc(status)}</GrpSts>${why}</OrgnlGrpInfAndSts></FIToFIPmtStsRpt></Document>`;
  }
  return head + `<OrgnlGrpInfAndSts><OrgnlMsgId>${esc(payment.msgId)}</OrgnlMsgId>`
    + `<OrgnlMsgNmId>${esc(payment.msgNmId || summary.messages['pacs.008'])}</OrgnlMsgNmId></OrgnlGrpInfAndSts>`
    + `<TxInfAndSts><OrgnlEndToEndId>${esc(payment.endToEndId)}</OrgnlEndToEndId>`
    + (payment.uetr ? `<OrgnlUETR>${esc(payment.uetr)}</OrgnlUETR>` : '')
    + `<TxSts>${esc(status)}</TxSts>${why}</TxInfAndSts></FIToFIPmtStsRpt></Document>`;
}

/** The first text of an element, whatever its prefix, or null. */
export function firstText(xml, tag) {
  const m = new RegExp(`<(?:[\\w-]+:)?${tag}(?:\\s[^>]*)?>([^<]*)</(?:[\\w-]+:)?${tag}>`).exec(xml || '');
  return m ? m[1].trim() : null;
}

/** An amount element as { amount, currency }, or null. */
export function firstAmount(xml, tag) {
  const m = new RegExp(`<(?:[\\w-]+:)?${tag}\\s+Ccy="([A-Z]{3})"[^>]*>([^<]*)</`).exec(xml || '');
  return m ? { amount: m[2].trim(), currency: m[1] } : null;
}

/**
 * What a message says, for a reader: its type, who it is from and to (in an
 * envelope), its status and reason, the scheme's fee, and the ids an answer
 * refers to.
 */
export function readMessage(xml) {
  if (!xml || !xml.trim()) return null;
  const hdr = /<(?:[\w-]+:)?AppHdr[\s>][\s\S]*?<\/(?:[\w-]+:)?AppHdr>/.exec(xml)?.[0] || '';
  const party = (side) => {
    const block = new RegExp(`<(?:[\\w-]+:)?${side}>[\\s\\S]*?</(?:[\\w-]+:)?${side}>`).exec(hdr)?.[0] || '';
    const id = firstText(block, 'MmbId') || firstText(block, 'BICFI') || firstText(block, 'Id');
    return id === 'NOTPROVIDED' ? null : id;
  };
  const charges = /<(?:[\w-]+:)?ChrgsInf>[\s\S]*?<\/(?:[\w-]+:)?ChrgsInf>/.exec(xml)?.[0];
  const why = /<(?:[\w-]+:)?StsRsnInf>[\s\S]*?<\/(?:[\w-]+:)?StsRsnInf>/.exec(xml)?.[0];
  return {
    type: firstText(hdr, 'MsgDefIdr') || (/xsd:([a-z]{4}\.\d{3}\.\d{3}\.\d{2})/.exec(xml)?.[1] ?? null),
    from: party('Fr'),
    to: party('To'),
    msgId: firstText(xml, 'MsgId'),
    groupStatus: firstText(xml, 'GrpSts'),
    txStatus: firstText(xml, 'TxSts'),
    reason: why ? firstText(why, 'Cd') : null,
    info: firstText(xml, 'AddtlInf'),
    fee: charges ? firstAmount(charges, 'Amt') : null,
    endToEndId: firstText(xml, 'EndToEndId'),
    uetr: firstText(xml, 'UETR'),
    settlement: firstAmount(xml, 'IntrBkSttlmAmt'),
    instructed: firstAmount(xml, 'InstdAmt'),
    signed: /SignatureValue/.test(xml),
  };
}

/** One line about a message: "XA0001 → RW0001 · pacs.002.001.07 · ACSC · fee 1000 RWF". */
export function describeMessage(m) {
  if (!m) return '';
  const parts = [];
  if (m.from || m.to) parts.push(`${m.from || '?'} → ${m.to || '?'}`);
  if (m.type) parts.push(m.type);
  const status = m.groupStatus || m.txStatus;
  if (status) parts.push(status + (m.reason ? ` ${m.reason}` : ''));
  if (m.fee) parts.push(`fee ${m.fee.amount} ${m.fee.currency}`);
  if (!status && m.settlement) parts.push(`${m.settlement.amount} ${m.settlement.currency}`);
  return parts.join(' · ');
}

/**
 * The message indented, one element per line, for reading and editing. Text
 * stays where it is, so what is signed carries the same values.
 */
export function prettyXml(xml) {
  if (!xml) return '';
  let depth = 0;
  return xml.replace(/>\s*</g, '>\n<').split('\n').map((node) => {
    if (/^<\//.test(node)) depth -= 1;
    const line = '  '.repeat(Math.max(depth, 0)) + node;
    const opens = /^<[^/?!][^>]*[^/]>$/.test(node) || /^<[^/?!]>$/.test(node);
    if (opens && !/<\/[^>]+>$/.test(node)) depth += 1;
    return line;
  }).join('\n');
}
